import { useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
const createPortalContainer = ({
  id,
  className,
  refDocument = document
}) => {
  const div = refDocument.createElement('div');
  div.setAttribute('id', id);
  div.setAttribute('role', 'dialog');
  div.setAttribute('aria-modal', 'true');
  div.setAttribute('aria-labelledby', `${id}-label`);
  div.setAttribute('tabindex', '-1');
  if (className) {
    // classes should be provided via the usage of `classNames` and can be a composite
    // of inherent classes of the portal'd component and passed `className` via prop
    className.split(' ').forEach(name => {
      div.classList.add(name);
    });
  }

  // append to DOM
  refDocument.body.insertBefore(div, refDocument.body.lastElementChild.nextElementSibling);
  return div;
};
const usePortal = ({
  id,
  className,
  refDocument = document
}) => {
  const rootElemRef = useRef(null);
  useEffect(() => {
    // look for existing target dom element to append to
    const existingParent = refDocument.querySelector(`#${id}`);
    // parent is either a new root or the existing dom element
    const parentElem = existingParent || createPortalContainer({
      id,
      className,
      refDocument
    });
    // add the to the parent
    parentElem.append(rootElemRef.current);
    return () => {
      rootElemRef.current.remove();
      if (parentElem.childNodes.length === 0) {
        parentElem.remove();
      }
    };
  }, [id, className, refDocument]);
  if (!rootElemRef.current) {
    rootElemRef.current = refDocument.createElement('div');
  }
  return rootElemRef.current;
};
export const Portal = ({
  id,
  children,
  className,
  refDocument = typeof window !== 'undefined' ? window.document : undefined
}) => {
  const target = usePortal({
    id,
    className,
    refDocument
  });
  return /*#__PURE__*/createPortal(children, target);
};